import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User } from '@/types';
import { authService } from '@/lib/auth';

export interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isMember: boolean;
  login: (email: string, password: string) => Promise<any>;
  register: (data: any) => Promise<any>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize refreshUser to prevent infinite re-renders
  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // If refresh fails, user might need to re-login
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const authData = await authService.login(email, password);
    setUser(authData.user);
    return authData;
  };

  const register = async (data: any) => {
    const response = await authService.register(data);
    return response;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const isAuthenticated = () => authService.isAuthenticated();
  const isAdmin = () => authService.isAdmin();
  const isMember = () => authService.isMember();
  const changePassword = async (currentPassword: string, newPassword: string, confirmPassword: string) => {
    await authService.changePassword(currentPassword, newPassword, confirmPassword);
  };

  useEffect(() => {
    // Check for stored user on mount
    const storedUser = authService.getStoredUser();
    const hasToken = localStorage.getItem('auth_token');
    
    if (storedUser && hasToken) {
      setUser(storedUser);
      // Optionally refresh user data from server to ensure it's current
      refreshUser();
    } else {
      setIsLoading(false);
    }
  }, [refreshUser]); // Now refreshUser is memoized, this won't cause infinite loops

  const value: AuthContextType = {
    user,
    setUser,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isMember: user?.role === 'member',
    login,
    register,
    logout,
    refreshUser,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
